import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import useAuthStore from '../../store/authStore';
import { updateProfile } from '../../api/auth';
import { getGoogleCalendarAuthUrl, getGoogleCalendarStatus, disconnectGoogleCalendar } from '../../api/googleCalendar';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };

const TutorSettingsPage = () => {
  const { user, setUser } = useAuthStore();

  const [name, setName] = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const queryClient = useQueryClient();

  const googleStatusQuery = useQuery({
    queryKey: ['google-calendar-status'],
    queryFn: getGoogleCalendarStatus,
    staleTime: 60_000,
  });

  const isGoogleConnected = googleStatusQuery.data?.connected ?? false;
  const googleEmail = googleStatusQuery.data?.googleEmail;

  const connectMutation = useMutation({
    mutationFn: getGoogleCalendarAuthUrl,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: () => enqueueSnackbar('Не вдалося отримати посилання Google', { variant: 'error' }),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-calendar-status'] });
      enqueueSnackbar('Google Calendar відключено', { variant: 'success' });
    },
    onError: () => enqueueSnackbar('Не вдалося відключити Google Calendar', { variant: 'error' }),
  });

  const nameMutation = useMutation({
    mutationFn: (payload) => updateProfile(payload),
    onSuccess: (data) => {
      setUser({ ...user, name: data.name });
      enqueueSnackbar('ПІБ оновлено', { variant: 'success' });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити ПІБ', { variant: 'error' }),
  });

  const passwordMutation = useMutation({
    mutationFn: (payload) => updateProfile(payload),
    onSuccess: () => {
      enqueueSnackbar('Пароль змінено', { variant: 'success' });
      setPassword('');
      setConfirmPassword('');
    },
    onError: () => enqueueSnackbar('Не вдалося змінити пароль', { variant: 'error' }),
  });

  const handleSaveName = () => {
    if (!name.trim()) {
      enqueueSnackbar("Введіть ПІБ", { variant: 'warning' });
      return;
    }
    nameMutation.mutate({ name: name.trim() });
  };

  const handleSavePassword = () => {
    if (password.length < 6) {
      enqueueSnackbar('Пароль повинен містити щонайменше 6 символів', { variant: 'warning' });
      return;
    }
    if (password !== confirmPassword) {
      enqueueSnackbar('Паролі не збігаються', { variant: 'warning' });
      return;
    }
    passwordMutation.mutate({ password });
  };

  return (
    <Stack spacing={2.5}>
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
            Дані та конфіденційність
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            Змініть своє ПІБ або пароль
          </Typography>
        </CardContent>
      </Card>

      {/* Name section */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#EDE9FE', color: B.primary }}>
              <PersonIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              ПІБ
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="Прізвище, ім'я, по батькові"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveName}
              disabled={nameMutation.isPending || name.trim() === user?.name}
              sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, textTransform: 'none', whiteSpace: 'nowrap', minWidth: 140 }}
            >
              Зберегти
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Google Calendar section */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#DBEAFE', color: '#2563EB' }}>
              <CalendarMonthIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              Google Calendar
            </Typography>
            {googleStatusQuery.isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <Chip
                label={isGoogleConnected ? 'Підключено' : 'Не підключено'}
                size="small"
                color={isGoogleConnected ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {isGoogleConnected && googleEmail
              ? `Підключено як ${googleEmail}. Заняття автоматично синхронізуються з Google Calendar.`
              : 'Синхронізуйте заплановані заняття з вашим Google Calendar. Нові, змінені та видалені заняття автоматично оновлюватимуться в календарі.'}
          </Typography>
          {isGoogleConnected ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              sx={{ borderRadius: 3, textTransform: 'none' }}
            >
              Відключити Google Calendar
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
              sx={{ bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' }, borderRadius: 3, textTransform: 'none' }}
            >
              Підключити Google Calendar
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Password section */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FEE2E2', color: '#EF4444' }}>
              <LockIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              Зміна пароля
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="Новий пароль"
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Підтвердження пароля"
              fullWidth
              size="small"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm((v) => !v)}>
                      {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePassword}
                disabled={passwordMutation.isPending || !password}
                sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, textTransform: 'none', minWidth: 180 }}
              >
                Змінити пароль
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default TutorSettingsPage;
